// src/Pages/TeamLeaderDetails.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Container, Paper, Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, TextField, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";

// TeamLeaderDetails Component
// This component displays a list of team leaders in a corporate-styled table.
// It includes a search field with Search and Reset buttons to filter team leaders.
// Clicking on a table row navigates to the team details page.
const TeamLeaderDetails_v1 = ({ loggedInUser, companyData }) => {
    const companyId = loggedInUser?.companyId;
    // Holds the list of team leaders fetched from the backend.
    const [teamLeaders, setTeamLeaders] = useState([]);
    // Controls whether data is currently being loaded.
    const [loading, setLoading] = useState(false);
    // Holds any error message resulting from data fetching.
    const [error, setError] = useState("");
    // Stores the search term entered by the user.
    const [searchTerm, setSearchTerm] = useState("");

    // useNavigate hook is used for programmatic navigation.
    const navigate = useNavigate();
    // API base URL is obtained from environment variables or defaults to 103.251.218.254.
    // const path = process.env.REACT_APP_path || "http://103.251.218.254:3100";
    const path = 'http://192.168.1.248:3103';

    /**
     * fetchTeamLeaders
     * ----------------
     * Fetches team leaders from the backend API.
     * @param {string} search - The search term to filter team leaders.
     */
    const fetchTeamLeaders = async (search = "") => {
        setLoading(true); // Start loading indicator
        try {
            // Make API request with the optional search term as a query parameter.
            const res = await axios.get(`${path}/api/teamleaders`, {
                params: { search, companyId }
            });
            if (res.data.success) {
                // If successful, update the teamLeaders state with the fetched data.
                setTeamLeaders(res.data.data);
                setError("");
            } else {
                // If API responds with failure, clear the team leaders list and show error message.
                setTeamLeaders([]);
                setError("No team leaders found.");
            }
        } catch (err) {
            // Log and set error message if an exception occurs during fetching.
            console.error("Error fetching team leaders:", err);
            setError("Server error while fetching team leaders.");
        }
        setLoading(false); // Stop loading indicator
    };

    // Fetch the initial list of team leaders when the component mounts.
    useEffect(() => {
        fetchTeamLeaders();
    }, []);

    /**
     * onSearchInputChange
     * -------------------
     * Event handler for updating the search term as the user types.
     * @param {object} e - The change event from the search input.
     */
    const onSearchInputChange = (e) => {
        setSearchTerm(e.target.value);
    };

    /**
     * handleSearchClick
     * -----------------
     * Called when the Search button is clicked.
     * Triggers fetching of team leaders using the current search term.
     */
    const handleSearchClick = () => {
        fetchTeamLeaders(searchTerm);
    };

    /**
     * handleResetClick
     * ----------------
     * Called when the Reset button is clicked.
     * Clears the search term and reloads the initial list of team leaders.
     */
    const handleResetClick = () => {
        setSearchTerm("");      // Clear the search input
        fetchTeamLeaders("");   // Fetch team leaders with an empty search term (initial data)
    };

    /**
     * handleLeaderClick
     * -----------------
     * Handles the row click event to navigate to the team details page.
     * @param {object} leader - The team leader associated with the clicked row.
     */
    const handleLeaderClick = (leader) => {
        navigate("/main/teamdetails", { state: { leaderId: leader.userId, leader } });
    };

    return (
        <Container sx={{ mt: 4 }}>
            {/* Page Title */}
            <Typography variant="h4" align="center" gutterBottom>
                Team Master
            </Typography>

            {/* Search Section */}
            <Box
                sx={{
                    mb: 2,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 2
                }}
            >
                {/* Search input field */}
                <TextField label="Search by Employee or User Name" variant="outlined" size="small" value={searchTerm} onChange={onSearchInputChange} sx={{ width: "100%", maxWidth: 400 }} />
                {/* Search button triggers API call with current search term */}
                <Button variant="contained" color="primary" onClick={handleSearchClick}>
                    Search
                </Button>
                {/* Reset button clears the search term and reloads initial data */}
                <Button variant="outlined" color="secondary" onClick={handleResetClick}>
                    Reset
                </Button>
            </Box>

            {/* Conditional Rendering:
          - Shows a loading spinner when data is being fetched.
          - Displays an error message if fetching fails.
          - Otherwise, displays the table of team leaders. */}
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Typography color="error" align="center">
                    {error}
                </Typography>
            ) : (
                <Paper sx={{ overflowX: "auto" }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Employee Name</TableCell>
                                <TableCell>User Name</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {teamLeaders.length > 0 ? (
                                teamLeaders.map((leader, index) => (
                                    // Clicking on the row triggers navigation to the team details page.
                                    <TableRow
                                        key={leader.userId}
                                        sx={{ cursor: "pointer" }}
                                        onClick={() => handleLeaderClick(leader)}
                                    >
                                        {/* Index column */}
                                        <TableCell sx={{ p: 1 }}>{index + 1}</TableCell>
                                        {/* Employee Name column */}
                                        <TableCell sx={{ p: 1 }}>{leader.employeeName}</TableCell>
                                        {/* User Name column */}
                                        <TableCell sx={{ p: 1 }}>{leader.userName}</TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                // If no team leaders are found, show a message.
                                <TableRow>
                                    <TableCell colSpan={3} align="center">
                                        <Typography color="textSecondary">
                                            No team leaders found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Paper>
            )}
        </Container>
    );
};

export default TeamLeaderDetails_v1;